import { Component, ChangeDetectionStrategy, ChangeDetectorRef, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { FormButtonType, ContainerBarcodeInfo } from './../service/form-field';
import { ShipmentFormStorage } from './../shipment-form-storage';
import { GridDetails } from '../../../sample-tracking/tracking-overview/interfaces/grid';
import { map, switchMap, concatMap } from 'rxjs/operators';
import { ConfirmDialogRequest } from '../../shared/confirm-dialog/confirm-dialog.component';
import { ShipmentFormService } from './../service/shipment-form-service';
import { SiteShippingBarcodeDetailsData } from './../service/container-barcode-info';
import { ContainerData, ContainerTransferData } from '../../interfaces/shipment-form-data';
import { iif } from 'rxjs';
import { UrlBuilder } from '../../../core';
import { ContainerGridData } from '../resume-shipment/resume-shipment-info';
import { ActivityLogger } from '../../../activities/activity-logger';

@Component({
    selector: 'container-barcode-handler',
    template: require('./container-barcode-handler.component.html'),
    styles: [require('./container-barcode-handler.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContainerBarcodeHandlerComponent {

    @Input()
    public containerFormControl: FormControl;
    @Input()
    public isResumeShipment: boolean;
    @Input()
    public gridDetails: GridDetails;
    @Input()
    public containerBarcodes: string[] = [];
    @Input()
    public containerData: ContainerData[] = [];
    @Input()
    public showShipmentDetails = false;
    @Input()
    public disableSubmit = false;
    @Input()
    public showSubmitLoading = false;
    @Input()
    public containerTransferReason: { description: string, transferReasonId: number }[] = [];
    @Input()
    public showSaveLoading = false;
    @Input()
    public airbillNumberExists = false;
    @Input()
    public shipmentId: number;
    @Output()
    public onBarcodeAdded: EventEmitter<ContainerBarcodeInfo> = new EventEmitter();
    @Output()
    public onButtonClick: EventEmitter<FormButtonType> = new EventEmitter();
    @Output()
    public onSubmitClick: EventEmitter<boolean> = new EventEmitter();
    public collapseSamples = false;
    public showBarcodeScanner = false;
    public errorMessage: string;
    public showSubmitAlert = false;
    public showBarcodeLoading = false;
    public sampleCount = 1;
    private containerTransferData: ContainerTransferData[] = [];
    private isEmulated: boolean;
    
    constructor(
        private storage: ShipmentFormStorage,
        private changeDetecterRef: ChangeDetectorRef,
        private createShipmentFormService: ShipmentFormService,
        private urlBuilder: UrlBuilder,
        private activityLogger: ActivityLogger,
    ) {
        this.isEmulated = this.createShipmentFormService.isUserEmulated();
    }

    public onContainerButtonClick(buttonType: FormButtonType) {
        this.collapseSamples = true;
        this.onButtonClick.emit(buttonType);
    }

    public openBarcodeScanner() {
        this.activityLogger.logActivity('Create a Shipment - Action : Camera Selection');
        this.showBarcodeScanner = !this.showBarcodeScanner;
    }

    public onSubmit() {
        this.onSubmitClick.emit(true);
    }

    public addContainer(barcodeNumber: string) {
        if (!barcodeNumber) {
            barcodeNumber = this.containerFormControl.value;
        }
        barcodeNumber = barcodeNumber.trim();
        let formatedBarcode = this.createShipmentFormService.formatBarcode(barcodeNumber);

        if (this.checkBarcodeExists(formatedBarcode.formattedBarcode) || this.isBarcodeAlreadyAdded(barcodeNumber)) {
            this.showWarning({ message: 'Container entered has already been added to current shipment', isWarning: true });
            return;
        }

        if (!formatedBarcode) { return; }
        this.showBarcodeLoading = true;
        this.storage.getContainerSearchData(formatedBarcode)
            .subscribe((res: any) => {
                const data = res.barcodeDetailsData as SiteShippingBarcodeDetailsData[];
                this.showBarcodeLoading = false;
                this.errorMessage = null;
                if (data && data.length) {
                    if (this.isBarcodeAlreadyAdded(data[0].barcodeRaw) || this.isBarcodeAlreadyAdded(data[0].accessionNumber + data[0].containerNumber)) {
                        this.showWarning({ message: 'Container entered has already been added to current shipment', isWarning: true });
                        return;
                    }
                    let containerData = data[0];
                    if (data.length > 1) {
                        let shippedData = data.filter(t => t.statusCode === 1);
                        containerData = shippedData && shippedData.length >= 1 ? shippedData[0] : containerData;
                    }
                    containerData.unFormattedBarcode = barcodeNumber;
                    this.handleContainerBarcodeWarning(containerData, formatedBarcode.formattedBarcode);
                }
            });
    }

    public barcodeEvent(event: any) {
        this.showBarcodeScanner = false;
        this.containerFormControl.setValue(event.barcodeData );
        if (!event.barcodeData) { return; }
        this.addContainer(event.barcodeData);
    }

    public onGridItemClick(event: any) {
        let barcode = event.data.barcodeRaw ? event.data.barcodeRaw : event.data.barcodeNumber;
        this.showWarning(
            {
                message: `Container <b>${barcode}</b> contains the following items. <br>
                Are you sure you would like to delete?  <br> <br>
                <b>Note: </b>This action cannot be undone.`,
                gridDetails: {
                    header: this.createShipmentFormService.getShipmentTableHeader().filter(h => h.key !== 'action'),
                    data: [event.data]
                },
                yesButtonText: 'Yes, Delete Container',
                reverseButtons: true,
                title: 'Confirm Deletion',
            },
            true
        ).subscribe(res => {
            if (res.isYesButtonClicked && !this.isEmulated) {
                let eventBarcode = event.data.userEnteredBarcode;
                this.containerBarcodes = this.containerBarcodes.filter(cb => cb !== eventBarcode);
                this.handleDeleteContainer(event.data, eventBarcode);
                this.containerTransferData = this.containerTransferData.filter(ctd => ctd.userEnteredBarcode !== eventBarcode);
                this.gridDetails = {
                    header: this.gridDetails.header,
                    data: this.gridDetails.data.filter((d: any) => d.userEnteredBarcode !== eventBarcode)
                };
                this.containerBarcodeAddedEvent();
                this.createShipmentFormService.setContainerValuesToLocalStorage(this.containerBarcodes);
                this.createShipmentFormService.setGridDataToLocalStorage(this.gridDetails);
                this.createShipmentFormService.setContainerTransferDataToLocalStorage(this.containerTransferData);
                (this.gridDetails.data.length === 0 && this.shipmentId && this.removeEmptyShipment());
            }
        });
    }

    public addRegisteredSamplesToShipment(data: SiteShippingBarcodeDetailsData[]) {
        data.forEach(sampleInfo => {
            this.containerBarcodes.push(sampleInfo.userEnteredBarcode);
            this.containerData.push({
                accessionId: sampleInfo.accessionId,
                barcode: sampleInfo.barcodeNumber,
                containerId: sampleInfo.containerId,
                kitItemSpecificationId: sampleInfo.kitItemSpecificationId,
                userEnteredBarcode: sampleInfo.userEnteredBarcode
            });
            this.gridDetails.data.unshift(sampleInfo);
        });
        this.gridDetails = { ...this.gridDetails };
        this.createShipmentFormService.setGridDataToLocalStorage(this.gridDetails);
        this.createShipmentFormService.setContainerValuesToLocalStorage(this.containerBarcodes);
        this.containerBarcodeAddedEvent();
        this.showAlert(data ? data.length : 0);
        this.changeDetecterRef.markForCheck();
    }

    public closeAlert() {
        this.showSubmitAlert = false;
        this.sampleCount = 1;
        this.changeDetecterRef.markForCheck();
    }

    private showAlert(count = 1) {
        this.showSubmitAlert = true;
        this.sampleCount = count;
        setTimeout(() => { this.closeAlert(); }, 3000);
    }

    private removeEmptyShipment() {
        if (!this.gridDetails.data.length) {
            this.showWarning(
                {
                    message: `This Shipment has No Containers, Are you sure you would like to delete this shipment? <br> <br>
                    <b>Note: </b>This action cannot be undone.`,
                    yesButtonText: 'Yes, Delete The Shipment',
                    reverseButtons: true,
                    title: 'Confirm Deletion',
                },
                true
            ).pipe(
                concatMap((dialogResult) =>
                    iif(() => dialogResult.isYesButtonClicked,
                        this.storage.deleteShipment(this.shipmentId)
                    )
                )
            ).subscribe(() => this.urlBuilder.esa().sampleManagement().siteShipping().submittedShipmentList().navigate());
        }
    }

    private showWarning(data: ConfirmDialogRequest, showDeleteDialog = false) {
        let dialogRequest: ConfirmDialogRequest = { ...data, noButtonText: 'Cancel' };
        return this.createShipmentFormService.showConfirmDialog(dialogRequest, showDeleteDialog)
            .pipe(switchMap(dialog => dialog.onHide()), map(dialogResult => dialogResult));
    }

    private addSampleToShipment(barcodeNumber: string, data: SiteShippingBarcodeDetailsData) {
        data = this.createShipmentFormService.formatShipmentDetails(data);
        this.containerBarcodes.push(barcodeNumber);
        this.setGridData(data);
        this.createShipmentFormService.setGridDataToLocalStorage(this.gridDetails);
        this.createShipmentFormService.setContainerValuesToLocalStorage(this.containerBarcodes);
        this.containerBarcodeAddedEvent();
        this.showAlert();
        this.clearBarcodeField();
    }

    private setGridData(data: SiteShippingBarcodeDetailsData) {
        this.gridDetails.data.unshift(data);
        this.gridDetails = { ...this.gridDetails };
        this.containerData.push({
            accessionId: data.accessionId,
            barcode: data.barcodeRaw,
            containerId: data.containerId,
            kitItemSpecificationId: data.kitItemSpecificationId,
            userEnteredBarcode: data.userEnteredBarcode
        });
        this.changeDetecterRef.markForCheck();
    }

    private checkBarcodeExists(barcodeNumber: string) {
        this.clearBarcodeField();
        return this.containerBarcodes.find(value => value === barcodeNumber) ? true : false;
    }

    private clearBarcodeField() {
        this.containerFormControl.setValue('');
    }

    private handleContainerBarcodeWarning(data: SiteShippingBarcodeDetailsData, barcodeNumber: string) {
        this.showSubmitAlert = false;
        if (data.statusCode === 0) {
            this.addSampleToShipment(barcodeNumber, data);
            return;
        }
        let { message, yesButtonText } = this.createShipmentFormService.getWarningMessage(data.statusCode, data);
        this.showWarning({ message, isWarning: false, yesButtonText }).subscribe(res => {
            if (!res.isCancelled) {
                if (data.statusCode === 1) {
                    this.showWarning(this.createShipmentFormService.getExistingShipmentWarning(this.containerTransferReason))
                        .subscribe(warningResult => {
                            if (!warningResult.isCancelled) {
                                this.containerTransferData.push(this.createShipmentFormService.getContainerTransferData(warningResult, data));
                                this.createShipmentFormService.setContainerTransferDataToLocalStorage(this.containerTransferData);
                                this.addSampleToShipment(barcodeNumber, data);
                                return;
                            }
                        });
                } else {
                    this.addSampleToShipment(barcodeNumber, data);
                    return;
                }
            }
        });
    }

    private isBarcodeAlreadyAdded(barcodeNumber: string) {
        return this.gridDetails.data.find((d: any) => {
            return (d.barcodeNumber.replace('-', '') === barcodeNumber.replace('-', '')
                || d.barcodeRaw === barcodeNumber.replace('-', '')
                || d.accessionNumber + d.containerNumber === barcodeNumber.replace('-', ''));
        });
    }

    private containerBarcodeAddedEvent() {
        let info: ContainerBarcodeInfo = {
            barcodes: this.containerBarcodes,
            containerTransferData: this.containerTransferData,
            containerData: this.containerData
        };
        this.onBarcodeAdded.emit(info);
    }

    private handleDeleteContainer(data: any, barcode: string) {
        this.containerData = this.containerData.filter(cd => cd.userEnteredBarcode !== barcode);
        if (data.investigatorsiteShipmentContainerId && this.shipmentId) {
            this.storage.deleteContainerFromShipment(this.shipmentId, data.investigatorsiteShipmentContainerId).subscribe();
        }
    }
}